import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface UserDetail {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  status: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  quota: {
    max_forms: number;
    max_fields_per_form: number;
    max_submissions_per_form: number;
    max_storage_bytes: number;
    max_file_size_bytes: number;
    max_api_keys: number;
    api_rate_limit_per_hour: number;
    max_groups: number;
    max_members_per_group: number;
    current_form_count: number;
    current_storage_bytes: number;
  } | null;
  usage: {
    forms: number;
    groups: number;
    apiKeys: number;
  };
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Edit form state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName: '',
    role: '' as 'superadmin' | 'admin' | 'user',
    status: '' as 'active' | 'suspended' | 'pending',
    emailVerified: false,
  });

  // Password reset
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadUser();
    }
  }, [id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/admin/users/${id}`);
      if (response.ok) {
        const data = await response.json() as UserDetail;
        setUser(data);
        setEditForm({
          displayName: data.displayName || '',
          role: data.role as 'superadmin' | 'admin' | 'user',
          status: data.status as 'active' | 'suspended' | 'pending',
          emailVerified: data.emailVerified,
        });
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to load user');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.patch(`/api/admin/users/${id}`, editForm);
      if (response.ok) {
        setSuccessMessage('User updated successfully');
        setEditMode(false);
        loadUser();
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to update user');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword) return;

    setSaving(true);
    setPasswordError(null);

    try {
      const response = await api.patch(`/api/admin/users/${id}`, { password: newPassword });
      if (response.ok) {
        setSuccessMessage('Password updated successfully');
        setShowPasswordModal(false);
        setNewPassword('');
      } else {
        const data = await response.json() as { error?: string };
        setPasswordError(data.error || 'Failed to update password');
      }
    } catch {
      setPasswordError('Failed to connect to server');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (!user || !confirm(`Are you sure you want to suspend ${user.email}?`)) return;

    try {
      const response = await api.patch(`/api/admin/users/${id}`, { status: 'suspended' });
      if (response.ok) {
        setSuccessMessage('User suspended');
        loadUser();
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to suspend user');
      }
    } catch {
      setError('Failed to connect to server');
    }
  };

  const handleActivate = async () => {
    if (!user) return;

    try {
      const response = await api.patch(`/api/admin/users/${id}`, { status: 'active' });
      if (response.ok) {
        setSuccessMessage('User activated');
        loadUser();
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to activate user');
      }
    } catch {
      setError('Failed to connect to server');
    }
  };

  const handleDelete = async (hard: boolean) => {
    if (!user) return;

    const message = hard
      ? `Are you sure you want to PERMANENTLY DELETE ${user.email}? This cannot be undone and will delete all their data.`
      : `Are you sure you want to suspend ${user.email}?`;

    if (!confirm(message)) return;

    try {
      const url = hard ? `/api/admin/users/${id}?hard=true` : `/api/admin/users/${id}`;
      const response = await api.delete(url);
      if (response.ok) {
        navigate('/superadmin/users');
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to delete user');
      }
    } catch {
      setError('Failed to connect to server');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <Link to="/superadmin/users" className="mt-4 inline-block text-primary-600 hover:underline">
          Back to Users
        </Link>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/superadmin/users"
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user.displayName || user.email}
            </h1>
            <p className="text-gray-500">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="btn-secondary"
            >
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditForm({
                    displayName: user.displayName || '',
                    role: user.role as 'superadmin' | 'admin' | 'user',
                    status: user.status as 'active' | 'suspended' | 'pending',
                    emailVerified: user.emailVerified,
                  });
                }}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700">
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="ml-4 text-green-500 hover:text-green-700">
            Dismiss
          </button>
        </div>
      )}

      {/* User Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">User Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-gray-900">{user.email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Display Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={editForm.displayName}
                    onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                    className="mt-1 input"
                    placeholder="No display name"
                  />
                ) : (
                  <p className="mt-1 text-gray-900">{user.displayName || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Role</label>
                {editMode ? (
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as typeof editForm.role })}
                    className="mt-1 input"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                ) : (
                  <p className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'superadmin'
                        ? 'bg-purple-100 text-purple-800'
                        : user.role === 'admin'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.role}
                    </span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                {editMode ? (
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as typeof editForm.status })}
                    className="mt-1 input"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending">Pending</option>
                  </select>
                ) : (
                  <p className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : user.status === 'suspended'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user.status}
                    </span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Email Verified</label>
                {editMode ? (
                  <label className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.emailVerified}
                      onChange={(e) => setEditForm({ ...editForm, emailVerified: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Verified</span>
                  </label>
                ) : (
                  <p className="mt-1">
                    {user.emailVerified ? (
                      <span className="text-green-600">Verified</span>
                    ) : (
                      <span className="text-red-600">Not Verified</span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">MFA</label>
                <p className="mt-1">
                  {user.mfaEnabled ? (
                    <span className="text-green-600">Enabled</span>
                  ) : (
                    <span className="text-gray-500">Disabled</span>
                  )}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Created</label>
                <p className="mt-1 text-gray-900">
                  {new Date(user.createdAt).toLocaleString()}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Last Login</label>
                <p className="mt-1 text-gray-900">
                  {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage Statistics</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-gray-900">{user.usage.forms}</p>
                <p className="text-sm text-gray-500">Forms</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-gray-900">{user.usage.groups}</p>
                <p className="text-sm text-gray-500">Groups</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-gray-900">{user.usage.apiKeys}</p>
                <p className="text-sm text-gray-500">API Keys</p>
              </div>
            </div>
          </div>

          {/* Quota */}
          {user.quota && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quota Limits & Usage</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Forms</p>
                  <p className="font-medium">
                    {user.quota.current_form_count} / {user.quota.max_forms}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Max Fields/Form</p>
                  <p className="font-medium">{user.quota.max_fields_per_form}</p>
                </div>
                <div>
                  <p className="text-gray-500">Max Submissions/Form</p>
                  <p className="font-medium">{user.quota.max_submissions_per_form.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-500">Storage</p>
                  <p className="font-medium">
                    {(user.quota.current_storage_bytes / 1024 / 1024).toFixed(1)} / {(user.quota.max_storage_bytes / 1024 / 1024).toFixed(0)} MB
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Max File Size</p>
                  <p className="font-medium">{(user.quota.max_file_size_bytes / 1024 / 1024).toFixed(0)} MB</p>
                </div>
                <div>
                  <p className="text-gray-500">Max API Keys</p>
                  <p className="font-medium">{user.quota.max_api_keys}</p>
                </div>
                <div>
                  <p className="text-gray-500">API Rate Limit</p>
                  <p className="font-medium">{user.quota.api_rate_limit_per_hour.toLocaleString()}/hr</p>
                </div>
                <div>
                  <p className="text-gray-500">Max Groups</p>
                  <p className="font-medium">{user.quota.max_groups}</p>
                </div>
                <div>
                  <p className="text-gray-500">Max Members/Group</p>
                  <p className="font-medium">{user.quota.max_members_per_group}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="space-y-3">
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full btn-secondary justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Reset Password
              </button>

              {user.status === 'active' ? (
                <button
                  onClick={handleSuspend}
                  className="w-full btn-secondary text-yellow-600 hover:bg-yellow-50 hover:border-yellow-300 justify-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  Suspend User
                </button>
              ) : (
                <button
                  onClick={handleActivate}
                  className="w-full btn-secondary text-green-600 hover:bg-green-50 hover:border-green-300 justify-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activate User
                </button>
              )}

              <hr className="my-4" />

              <button
                onClick={() => handleDelete(false)}
                className="w-full btn-secondary text-red-600 hover:bg-red-50 hover:border-red-300 justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Soft Delete (Suspend)
              </button>

              <button
                onClick={() => handleDelete(true)}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Permanently Delete
              </button>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Info</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500">User ID</p>
                <p className="font-mono text-xs text-gray-700 break-all">{user.id}</p>
              </div>
              <div>
                <p className="text-gray-500">Last Updated</p>
                <p className="text-gray-700">{new Date(user.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Reset Password</h2>
            <form onSubmit={handleResetPassword}>
              {passwordError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {passwordError}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input w-full"
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum 8 characters with uppercase, lowercase, and number
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setPasswordError(null);
                  }}
                  className="flex-1 btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={saving || !newPassword}
                >
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
